import { useEffect, useRef } from "react"
import type { ThreeEvent } from "@react-three/fiber"
import { SOCKET_EVENTS, type Vector3Tuple } from "@domo/shared"
import { getSocket } from "../../lib/socket"
import {
    WALK_BOUNDARY_RADIUS,
    clampToBoundary,
} from "../../lib/domeConstants"
import { usePlayerStore } from "../../stores/usePlayerStore"

const PLANE_SIZE = 80
const HALF = PLANE_SIZE / 2

// Delay para distinguir tap de pinch. Cuando el Tripulante hace pinch
// con dos dedos para zoom, el primer pointerdown se dispara ANTES que
// el segundo. Si ejecutáramos handleClick inmediato, el avatar
// caminaría al primer punto antes de que llegue el segundo dedo. Con
// 90ms de espera podemos detectar el segundo pointer (que sube
// activePointersCount) y cancelar. 90ms es imperceptible para tap
// normal pero suficiente para que iOS/Android registre el segundo
// dedo del pinch.
const PINCH_GUARD_MS = 90

// Plano invisible que captura los clicks del tripulante.
//
// Está sobre el suelo a y=0, mismo nivel que el Floor visible. Como es
// transparente y no tiene z-fight (el suelo está -0.001), no se ve
// pero sí captura el ray.
//
// Al tap simple → setea target en el store (predicción local) + emite
// al server. Al pinch (multi-touch) → ignoramos para no mover el
// avatar mientras el Tripulante hace zoom de cámara.
export function MovementPlane() {
    /* Trackeador global de pointers activos. Usamos un ref + window
       listeners en lugar de un Set local porque el plano vive dentro
       del Canvas R3F y los eventos pueden llegar al canvas/document
       con cualquier orden. Window-level captura todo. */
    const pointersRef = useRef(0)

    useEffect(() => {
        const onDown = () => {
            pointersRef.current = Math.max(pointersRef.current + 1, 1)
        }
        const onUpOrCancel = () => {
            pointersRef.current = Math.max(pointersRef.current - 1, 0)
        }
        window.addEventListener("pointerdown", onDown)
        window.addEventListener("pointerup", onUpOrCancel)
        window.addEventListener("pointercancel", onUpOrCancel)
        /* Defensa adicional: cuando el tab pierde foco (pointercancel
           a veces no llega en iOS Safari al rotar/cambiar app), reset. */
        const onVis = () => {
            if (document.hidden) pointersRef.current = 0
        }
        document.addEventListener("visibilitychange", onVis)
        return () => {
            window.removeEventListener("pointerdown", onDown)
            window.removeEventListener("pointerup", onUpOrCancel)
            window.removeEventListener("pointercancel", onUpOrCancel)
            document.removeEventListener("visibilitychange", onVis)
        }
    }, [])

    // v3 — onClick de R3F dispara incluso después de un drag amplio
    // (el threshold interno es permisivo). Cuando OrbitControls rota
    // la cámara con left-click drag, al soltar el botón el avatar
    // salía caminando hacia el último punto bajo el cursor. Solución:
    // capturamos la posición de pantalla en pointerdown y validamos
    // delta < TAP_THRESHOLD_PX en pointerup. Si hubo más movimiento,
    // fue drag (rotación) y NO disparamos move.
    const TAP_THRESHOLD_PX = 6
    const downXY = useRef<{ x: number; y: number; pointId: number } | null>(
        null
    )

    const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
        downXY.current = {
            x: e.clientX,
            y: e.clientY,
            pointId: e.pointerId,
        }
    }

    const handlePointerUp = (e: ThreeEvent<PointerEvent>) => {
        const start = downXY.current
        downXY.current = null
        if (!start || start.pointId !== e.pointerId) return
        const dx = Math.abs(e.clientX - start.x)
        const dy = Math.abs(e.clientY - start.y)
        if (dx > TAP_THRESHOLD_PX || dy > TAP_THRESHOLD_PX) {
            // Fue drag: OrbitControls rotó la cámara, NO movemos.
            return
        }
        e.stopPropagation()

        const point = e.point
        // 2026-05-05 v6 — Doble clamp del clic.
        // (1) Caja del plano (HALF) por seguridad: no manda al avatar
        //     a coordenadas absurdas si el raycast pega lejos.
        // (2) Círculo del Domo (WALK_BOUNDARY_RADIUS): si el dedo cae
        //     fuera del piso del Domo, proyectamos al borde del
        //     círculo. El avatar nunca camina más allá del biopolímero.
        const xBox = Math.max(-HALF + 1, Math.min(HALF - 1, point.x))
        const zBox = Math.max(-HALF + 1, Math.min(HALF - 1, point.z))
        const clamped = clampToBoundary(xBox, zBox, WALK_BOUNDARY_RADIUS)
        const target: Vector3Tuple = [clamped.x, 0, clamped.z]

        // Pinch guard: si en PINCH_GUARD_MS aparecen más pointers,
        // asumimos pinch (touch) y cancelamos el move. En desktop con
        // mouse este branch nunca aplica.
        const baseline = pointersRef.current
        window.setTimeout(() => {
            if (pointersRef.current > baseline) return
            usePlayerStore.getState().setTarget(target)

            const socket = getSocket()
            if (socket?.connected) {
                socket.emit(SOCKET_EVENTS.PLAYER_MOVE_CLICK, {
                    target,
                    ts: Date.now(),
                })
            }
        }, PINCH_GUARD_MS)
    }

    // visible=true con opacity=0 — el raycaster ignora meshes
    // invisibles, así que mantenemos visibilidad pero el material es
    // completamente transparente.
    return (
        <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, 0.005, 0]}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
        >
            <planeGeometry args={[PLANE_SIZE, PLANE_SIZE]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
    )
}
